/*
* Copyright Amazon.com, Inc. and its affiliates. All Rights Reserved.
* SPDX-License-Identifier: MIT
*
* Licensed under the MIT License. See the LICENSE accompanying this file
* for the specific language governing permissions and limitations under
* the License.
*/

import React, { useState } from 'react';
import { Auth } from 'aws-amplify';
import { I18n } from '@aws-amplify/core';
import { AuthState } from '@aws-amplify/ui-components';

import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import InputAdornment from '@material-ui/core/InputAdornment';
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';

import { MailOutline } from '@material-ui/icons';

import { Branding } from '../../branding';
import FormInlineError from '../FormInlineError/FormInlineError';
import PasswordInputField, { isPasswordMatch, validatePasswordRules, PASSWORD_RULES } from '../PasswordInputField/PasswordInputField';
import { translateFormErrorMessage } from '../../utils/formErrorMessage';
import { readInputValueById } from '../../utils/reactFormSync';

const useStyles = makeStyles((theme) => ({
	root: {
		boxSizing: 'border-box',
		width: '100%',
		maxWidth: 460, // AmplifySignInコンポーネントと同等の幅。狭い画面では親幅に収める
	},
	title: {
		marginBottom: theme.spacing(3),
		fontWeight: 'bold',
	},
	box: {
		marginBottom: theme.spacing(2),
	},
	formControl: {
		width: '100%',
	},
	input: {
		width: '100%',
	},
	textFieldIcon: {
		color: Branding.secondary,
	},
	buttonSubmit: {
		width: '100%',
		marginTop: theme.spacing(2),
		backgroundColor: Branding.secondary,
		color: Branding.white,
		'&:hover': {
			backgroundColor: Branding.secondary,
			opacity: Branding.opacityHover,
		},
		'&:disabled': {
			backgroundColor: '#ccc',
			color: '#666',
		},
	},
	linkBackToSignIn: {
		marginTop: theme.spacing(2),
		display: 'block',
		color: Branding.secondary,
		cursor: 'pointer',
		'&:hover': {
			color: Branding.accent,
		},
	},
	passwordHint: {
		fontSize: '0.85rem',
		color: '#666',
		marginBottom: theme.spacing(1),
	},
	sendCodeHint: {
		...theme.typography.body1,
		color: Branding.secondary,
		marginTop: theme.spacing(3),
		textAlign: 'left',
	},
	validationError: {
		fontSize: '0.85rem',
		color: Branding.negative,
		marginTop: theme.spacing(0.5),
	},
	validationSuccess: {
		fontSize: '0.85rem',
		color: Branding.positive,
		marginTop: theme.spacing(0.5),
	},
}));

const ForgotPassword = (props) => {
	const classes = useStyles();
	const [step, setStep] = useState('REQUEST'); // REQUEST or SUBMIT
	const [username, setUsername] = useState('');
	const [code, setCode] = useState('');
	const [newPassword, setNewPassword] = useState({ password: '', showPassword: false });
	const [confirmPassword, setConfirmPassword] = useState({ password: '', showPassword: false });
	const [error, setError] = useState('');
	const [loading, setLoading] = useState(false);

	const handleSendCode = async () => {
		const resolvedUsername = readInputValueById('inputUsername', username);
		setUsername(resolvedUsername);
		if (!resolvedUsername) {
			setError(I18n.get('FORGOT_PASSWORD_USERNAME_LABEL') + 'を入力してください');
			return;
		}

		setLoading(true);
		setError('');

		try {
			await Auth.forgotPassword(resolvedUsername);
			setStep('SUBMIT');
		} catch (err) {
			console.error(err);
			setError(translateFormErrorMessage(err.message || 'エラーが発生しました'));
		} finally {
			setLoading(false);
		}
	};

	// パスワードのバリデーション結果を取得
	const passwordValidation = validatePasswordRules(newPassword.password);
	const passwordsMatch = isPasswordMatch(newPassword.password, confirmPassword.password);

	const handleSubmitNewPassword = async () => {
		const resolvedCode = readInputValueById('inputCode', code);
		const resolvedNewPassword = readInputValueById('inputNewPassword', newPassword.password);
		const resolvedConfirmPassword = readInputValueById('inputConfirmPassword', confirmPassword.password);
		setCode(resolvedCode);
		setNewPassword({ ...newPassword, password: resolvedNewPassword });
		setConfirmPassword({ ...confirmPassword, password: resolvedConfirmPassword });

		const resolvedValidation = validatePasswordRules(resolvedNewPassword);
		const resolvedMatch = isPasswordMatch(resolvedNewPassword, resolvedConfirmPassword);

		if (!resolvedCode || !resolvedNewPassword) {
			setError('認証コードと新しいパスワードを入力してください');
			return;
		}

		if (!resolvedValidation.isValid) {
			setError(resolvedValidation.messages.join('、'));
			return;
		}

		if (!resolvedMatch) {
			setError('パスワードが一致しません');
			return;
		}

		setLoading(true);
		setError('');

		try {
			await Auth.forgotPasswordSubmit(username, resolvedCode, resolvedNewPassword);
			// パスワードリセット成功、サインイン画面に戻る
			if (props.handleAuthStateChange) {
				props.handleAuthStateChange(AuthState.SignIn);
			} else {
				// AmplifyAuthenticatorのハブを使用
				const { Hub } = await import('aws-amplify');
				Hub.dispatch('UI Auth', {
					event: 'AuthStateChange',
					message: AuthState.SignIn,
				});
				// ページリロードでサインイン画面に戻る
				window.location.reload();
			}
		} catch (err) {
			console.error(err);
			setError(translateFormErrorMessage(err.message || 'エラーが発生しました'));
		} finally {
			setLoading(false);
		}
	};

	const handleBackToSignIn = () => {
		if (props.handleAuthStateChange) {
			props.handleAuthStateChange(AuthState.SignIn);
		} else {
			window.location.reload();
		}
	};

	// REQUEST step - ユーザー名入力と認証コード送信
	if (step === 'REQUEST') {
		return (
			<div className={classes.root}>
				<FormInlineError message={error} />
				<Typography variant="h5" className={classes.title}>
					{I18n.get('FORGOT_PASSWORD_TITLE')}
				</Typography>

				<Box className={classes.box}>
					<FormControl className={classes.formControl}>
						<InputLabel htmlFor="inputUsername">
							{I18n.get('FORGOT_PASSWORD_USERNAME_LABEL')}
						</InputLabel>
						<Input
							value={username}
							onChange={(e) => setUsername(e.target.value)}
							id="inputUsername"
							placeholder={I18n.get('FORGOT_PASSWORD_USERNAME_PLACEHOLDER')}
							startAdornment={
								<InputAdornment position="start">
									<MailOutline className={classes.textFieldIcon} />
								</InputAdornment>
							}
							className={classes.input}
						/>
					</FormControl>
				</Box>

				<Button
					variant="contained"
					className={classes.buttonSubmit}
					onClick={handleSendCode}
					disabled={loading}
				>
					{I18n.get('FORGOT_PASSWORD_SEND_CODE_BUTTON')}
				</Button>

				<Typography className={classes.sendCodeHint}>
					{I18n.get('FORGOT_PASSWORD_SEND_CODE_HINT')}
				</Typography>

				<Link
					className={classes.linkBackToSignIn}
					onClick={handleBackToSignIn}
				>
					{I18n.get('FORGOT_PASSWORD_BACK_TO_SIGNIN')}
				</Link>
			</div>
		);
	}

	// SUBMIT step - 認証コードと新しいパスワード入力
	return (
		<div className={classes.root}>
			<FormInlineError message={error} />
			<Typography variant="h5" className={classes.title}>
				{I18n.get('FORGOT_PASSWORD_TITLE')}
			</Typography>

			<Box className={classes.box}>
				<FormControl className={classes.formControl}>
					<InputLabel htmlFor="inputCode">
						{I18n.get('FORGOT_PASSWORD_CODE_LABEL')}
					</InputLabel>
					<Input
						value={code}
						onChange={(e) => setCode(e.target.value)}
						id="inputCode"
						placeholder={I18n.get('FORGOT_PASSWORD_CODE_PLACEHOLDER')}
						className={classes.input}
					/>
				</FormControl>
			</Box>

			<Box className={classes.box}>
				<Typography className={classes.passwordHint}>
					※ パスワードは{PASSWORD_RULES.MIN_LENGTH}〜{PASSWORD_RULES.MAX_LENGTH}文字で、英大文字・小文字・数字・記号のうち2種類以上を含めてください
				</Typography>
				<PasswordInputField
					id="inputNewPassword"
					label={I18n.get('FORGOT_PASSWORD_NEW_PASSWORD_LABEL')}
					placeholder={I18n.get('FORGOT_PASSWORD_NEW_PASSWORD_PLACEHOLDER')}
					value={newPassword.password}
					showPassword={newPassword.showPassword}
					onChange={(value) => setNewPassword({ ...newPassword, password: value })}
					onToggleShowPassword={() => setNewPassword({ ...newPassword, showPassword: !newPassword.showPassword })}
				/>
				{newPassword.password && !passwordValidation.isValid && (
					<div>
						{passwordValidation.messages.map((msg, index) => (
							<Typography key={index} className={classes.validationError}>
								{msg}
							</Typography>
						))}
					</div>
				)}
				{newPassword.password && passwordValidation.isValid && (
					<Typography className={classes.validationSuccess}>
						パスワードルールを満たしています
					</Typography>
				)}
			</Box>

			<Box className={classes.box}>
				<PasswordInputField
					id="inputConfirmPassword"
					label={I18n.get('FORGOT_PASSWORD_CONFIRM_PASSWORD_LABEL')}
					placeholder={I18n.get('FORGOT_PASSWORD_CONFIRM_PASSWORD_PLACEHOLDER')}
					value={confirmPassword.password}
					showPassword={confirmPassword.showPassword}
					onChange={(value) => setConfirmPassword({ ...confirmPassword, password: value })}
					onToggleShowPassword={() => setConfirmPassword({ ...confirmPassword, showPassword: !confirmPassword.showPassword })}
				/>
				{confirmPassword.password && !passwordsMatch && (
					<Typography className={classes.validationError}>
						パスワードが一致しません
					</Typography>
				)}
				{confirmPassword.password && passwordsMatch && (
					<Typography className={classes.validationSuccess}>
						パスワードが一致しています
					</Typography>
				)}
			</Box>

			<Button
				variant="contained"
				className={classes.buttonSubmit}
				onClick={handleSubmitNewPassword}
				disabled={loading}
			>
				{I18n.get('FORGOT_PASSWORD_SUBMIT_BUTTON')}
			</Button>

			<Link
				className={classes.linkBackToSignIn}
				onClick={handleBackToSignIn}
			>
				{I18n.get('FORGOT_PASSWORD_BACK_TO_SIGNIN')}
			</Link>
		</div>
	);
}

export default ForgotPassword;
